import { lazy, Suspense } from 'react';
import { createBrowserRouter, Outlet } from 'react-router-dom';
import { ProtectedRoute } from './ProtectedRoute';
import { PublicRoute } from './PublicRoute';
import { RequireRole } from './RequireRole';
import { LoadingScreen } from '@/components/ui/LoadingScreen';

// Lazy load all pages for code splitting
const Welcome = lazy(() => import('@/pages/Welcome').then(m => ({ default: m.Welcome })));
const NotFound = lazy(() => import('@/pages/NotFound').then(m => ({ default: m.NotFound })));

// Auth pages (lazy loaded)
const Register = lazy(() => import('@/pages/auth/Register').then(m => ({ default: m.Register })));
const Login = lazy(() => import('@/pages/auth/Login').then(m => ({ default: m.Login })));
const VerifyOtp = lazy(() => import('@/pages/auth/VerifyOtp').then(m => ({ default: m.VerifyOtp })));
const SetupBusiness = lazy(() => import('@/pages/auth/SetupBusiness').then(m => ({ default: m.SetupBusiness })));
const PinLoginPage = lazy(() => import('@/pages/auth/PinLoginPage').then(m => ({ default: m.PinLoginPage })));
const PasskeyLoginPage = lazy(() => import('@/pages/auth/PasskeyLoginPage').then(m => ({ default: m.PasskeyLoginPage })));

// Auth routing
const AuthRouter = lazy(() => import('@/components/auth/AuthRouter').then(m => ({ default: m.AuthRouter })));

// App pages (lazy loaded)
const Dashboard = lazy(() => import('@/pages/Dashboard').then(m => ({ default: m.Dashboard })));
const ProductList = lazy(() => import('@/pages/products/ProductList').then(m => ({ default: m.ProductList })));
const ProductCreate = lazy(() => import('@/pages/products/ProductCreate').then(m => ({ default: m.ProductCreate })));
const ProductDetail = lazy(() => import('@/pages/products/ProductDetail').then(m => ({ default: m.ProductDetail })));
const ProductEdit = lazy(() => import('@/pages/products/ProductEdit').then(m => ({ default: m.ProductEdit })));
const POS = lazy(() => import('@/pages/sales/POS').then(m => ({ default: m.POS })));
const SalesHistory = lazy(() => import('@/pages/sales/SalesHistory').then(m => ({ default: m.SalesHistory })));
const SaleDetailPage = lazy(() => import('@/pages/sales/SaleDetailPage').then(m => ({ default: m.SaleDetailPage })));
const CustomerList = lazy(() => import('@/pages/customers/CustomerList').then(m => ({ default: m.CustomerList })));
const CustomerProfile = lazy(() => import('@/pages/customers/CustomerProfile').then(m => ({ default: m.CustomerProfile })));
const Debtors = lazy(() => import('@/pages/credit/Debtors').then(m => ({ default: m.Debtors })));
const ExpenseList = lazy(() => import('@/pages/expenses/ExpenseList').then(m => ({ default: m.ExpenseList })));
const ExpenseDetailPage = lazy(() => import('@/pages/expenses/ExpenseDetailPage').then(m => ({ default: m.ExpenseDetailPage })));
const ExpenseSummaryPage = lazy(() => import('@/pages/expenses/ExpenseSummaryPage').then(m => ({ default: m.ExpenseSummaryPage })));
const ExpenseCategoriesPage = lazy(() => import('@/pages/expenses/ExpenseCategoriesPage').then(m => ({ default: m.ExpenseCategoriesPage })));
const ReportsHub = lazy(() => import('@/pages/reports/ReportsHub').then(m => ({ default: m.ReportsHub })));
const SalesReport = lazy(() => import('@/pages/reports/SalesReport').then(m => ({ default: m.SalesReport })));
const ProfitLoss = lazy(() => import('@/pages/reports/ProfitLoss').then(m => ({ default: m.ProfitLoss })));
const CashFlow = lazy(() => import('@/pages/reports/CashFlow').then(m => ({ default: m.CashFlow })));
const InventoryReport = lazy(() => import('@/pages/reports/InventoryReport').then(m => ({ default: m.InventoryReport })));
const LoanPack = lazy(() => import('@/pages/reports/LoanPack').then(m => ({ default: m.LoanPack })));
const VatReportsPage = lazy(() => import('@/pages/reports/VatReportsPage').then(m => ({ default: m.VatReportsPage })));
const Settings = lazy(() => import('@/pages/Settings').then(m => ({ default: m.Settings })));
const LoginSecurityPage = lazy(() => import('@/pages/settings/LoginSecurityPage').then(m => ({ default: m.LoginSecurityPage })));
const StaffPage = lazy(() => import('@/pages/staff/StaffPage').then(m => ({ default: m.StaffPage })));
const BranchesPage = lazy(() => import('@/pages/branches/BranchesPage').then(m => ({ default: m.BranchesPage })));
const TerminalsPage = lazy(() => import('@/pages/terminals/TerminalsPage').then(m => ({ default: m.TerminalsPage })));
const AcceptInvite = lazy(() => import('@/pages/auth/AcceptInvite').then(m => ({ default: m.AcceptInvite })));
const PublicReceipt = lazy(() => import('@/pages/receipts/PublicReceipt').then(m => ({ default: m.PublicReceipt })));
const LoanPackVerify = lazy(() => import('@/pages/reports/LoanPackVerify').then(m => ({ default: m.LoanPackVerify })));
const LoanPackVerifyPayment = lazy(() => import('@/pages/reports/LoanPackVerifyPayment').then(m => ({ default: m.LoanPackVerifyPayment })));
const MorePage = lazy(() => import('@/pages/MorePage').then(m => ({ default: m.MorePage })));
const SubscriptionPage = lazy(() => import('@/pages/SubscriptionPage').then(m => ({ default: m.SubscriptionPage })));
const PlanComparisonPage = lazy(() => import('@/pages/PlanComparisonPage').then(m => ({ default: m.PlanComparisonPage })));
const SubscriptionSuccessPage = lazy(() => import('@/pages/SubscriptionSuccessPage').then(m => ({ default: m.SubscriptionSuccessPage })));
const BillingHistoryPage = lazy(() => import('@/pages/BillingHistoryPage').then(m => ({ default: m.BillingHistoryPage })));
const InvoiceDetailPage = lazy(() => import('@/pages/InvoiceDetailPage').then(m => ({ default: m.InvoiceDetailPage })));
const PaymentMethodsPage = lazy(() => import('@/pages/PaymentMethodsPage').then(m => ({ default: m.PaymentMethodsPage })));

// Customer invoices (Phase 1 — separate from billing/subscription invoices above)
const InvoicesListPage = lazy(() => import('@/pages/invoices/InvoicesListPage').then(m => ({ default: m.InvoicesListPage })));
const InvoiceFormPage = lazy(() => import('@/pages/invoices/InvoiceFormPage').then(m => ({ default: m.InvoiceFormPage })));
const CustomerInvoiceDetailPage = lazy(() => import('@/pages/invoices/InvoiceDetailPage').then(m => ({ default: m.InvoiceDetailPage })));
const PublicInvoicePage = lazy(() => import('@/pages/public/PublicInvoicePage').then(m => ({ default: m.PublicInvoicePage })));
const InvoicingPage = lazy(() => import('@/pages/settings/InvoicingPage').then(m => ({ default: m.InvoicingPage })));

// Layout
const AppLayout = lazy(() => import('@/components/layout/AppLayout').then(m => ({ default: m.AppLayout })));

// Test pages (dev only)
// Dev-only test pages. The dynamic imports live inside the DEV conditional so
// Vite drops the chunks from production builds entirely — a top-level lazy()
// here would keep them in the build graph (and the SW precache) even with the
// routes removed.
const AppLayoutTest = import.meta.env.DEV
  ? lazy(() => import('@/pages/test/AppLayoutTest').then(m => ({ default: m.AppLayoutTest })))
  : (): null => null;
const MetricCardTest = import.meta.env.DEV
  ? lazy(() => import('@/pages/test/MetricCardTest').then(m => ({ default: m.MetricCardTest })))
  : (): null => null;

/**
 * SuspenseWrapper provides a consistent loading experience for lazy-loaded routes
 */
function SuspenseWrapper({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <Suspense fallback={<LoadingScreen />}>
      {children}
    </Suspense>
  );
}

/**
 * AuthLayout wraps public auth pages (login, register, etc.)
 * Redirects to dashboard if already authenticated
 */
function AuthLayout(): React.ReactElement {
  return (
    <PublicRoute>
      <SuspenseWrapper>
        <Outlet />
      </SuspenseWrapper>
    </PublicRoute>
  );
}

/**
 * ProtectedLayout wraps authenticated app pages
 * Redirects to login if not authenticated
 *
 * Note: AppLayout now uses React Router's Outlet internally,
 * so we don't need to wrap children here. AppLayout renders
 * <Outlet /> for nested route content.
 */
function ProtectedLayout(): React.ReactElement {
  return (
    <ProtectedRoute>
      <SuspenseWrapper>
        <AppLayout />
      </SuspenseWrapper>
    </ProtectedRoute>
  );
}

/**
 * Router configuration for the SabiBooks PWA
 *
 * Route structure:
 * - /auth/* - Public authentication routes
 * - / - Protected app routes (requires authentication)
 * - /receipt/:id - Public receipt view
 * - * - 404 Not Found
 */
export const router = createBrowserRouter([
  // Welcome/Landing page (redirects based on auth status)
  {
    path: '/welcome',
    element: (
      <SuspenseWrapper>
        <Welcome />
      </SuspenseWrapper>
    ),
  },

  // Public auth routes
  {
    path: '/auth',
    element: <AuthLayout />,
    children: [
      {
        index: true,
        element: <AuthRouter />,
      },
      {
        path: 'register',
        element: <Register />,
      },
      {
        path: 'login',
        element: <Login />,
      },
      {
        path: 'passkey',
        element: <PasskeyLoginPage />,
      },
      {
        path: 'pin',
        element: <PinLoginPage />,
      },
      {
        path: 'verify',
        element: <VerifyOtp />,
      },
      {
        path: 'setup',
        element: <SetupBusiness />,
      },
    ],
  },

  // Protected app routes
  {
    path: '/',
    element: <ProtectedLayout />,
    children: [
      {
        index: true,
        element: <Dashboard />,
      },
      // Products
      {
        path: 'products',
        element: <ProductList />,
      },
      {
        path: 'products/new',
        element: <ProductCreate />,
      },
      {
        path: 'products/:id',
        element: <ProductDetail />,
      },
      {
        path: 'products/:id/edit',
        element: <ProductEdit />,
      },
      // Sales/POS
      {
        path: 'sales/new',
        element: <POS />,
      },
      {
        path: 'sales',
        element: <SalesHistory />,
      },
      {
        path: 'sales/:id',
        element: <SaleDetailPage />,
      },
      // Customers
      {
        path: 'customers',
        element: <CustomerList />,
      },
      {
        path: 'customers/:id',
        element: <CustomerProfile />,
      },
      // Customer Invoices (Phase 1)
      {
        path: 'invoices',
        element: <InvoicesListPage />,
      },
      {
        path: 'invoices/new',
        element: <InvoiceFormPage mode="new" />,
      },
      {
        path: 'invoices/:id',
        element: <CustomerInvoiceDetailPage />,
      },
      {
        path: 'invoices/:id/edit',
        // Editing is OWNER/MANAGER (PATCH /invoices is gated); creation stays
        // open — cashiers write invoices at the counter by design.
        element: <RequireRole roles={['OWNER', 'MANAGER']} audience="owners and managers"><InvoiceFormPage mode="edit" /></RequireRole>,
      },
      // Credit/Debtors
      {
        path: 'credit',
        element: <Debtors />,
      },
      // Expenses
      {
        path: 'expenses',
        element: <ExpenseList />,
      },
      {
        path: 'expenses/summary',
        element: <ExpenseSummaryPage />,
      },
      {
        path: 'expenses/categories',
        element: <ExpenseCategoriesPage />,
      },
      {
        path: 'expenses/:id',
        element: <ExpenseDetailPage />,
      },
      // Reports
      {
        path: 'reports',
        element: <ReportsHub />,
      },
      {
        path: 'reports/sales',
        element: <SalesReport />,
      },
      {
        path: 'reports/profit-loss',
        element: <ProfitLoss />,
      },
      {
        path: 'reports/cash-flow',
        element: <CashFlow />,
      },
      {
        path: 'reports/inventory',
        element: <InventoryReport />,
      },
      {
        path: 'reports/loan-pack',
        element: <LoanPack />,
      },
      {
        // Paystack one-shot checkout callback for the loan-pack unlock.
        // Path must match LoanPackProperties.callbackUrl on the backend.
        path: 'reports/loan-pack/verify',
        element: <LoanPackVerifyPayment />,
      },
      {
        path: 'reports/vat',
        element: <VatReportsPage />,
      },
      // More hub (mobile navigation)
      {
        path: 'more',
        element: <MorePage />,
      },
      // Settings
      {
        path: 'settings',
        element: <Settings />,
      },
      // Login & Security
      {
        path: 'settings/login-security',
        element: <LoginSecurityPage />,
      },
      // Staff Management (owner only)
      {
        path: 'settings/staff',
        element: <StaffPage />,
      },
      // Branch Management (owner only)
      {
        path: 'settings/branches',
        element: <BranchesPage />,
      },
      // POS Terminal Management (owner + manager)
      {
        path: 'settings/terminals',
        element: <TerminalsPage />,
      },
      // Invoicing settings (bank fields, payment terms, prefix, VAT mode — owner only)
      {
        path: 'settings/invoicing',
        element: <InvoicingPage />,
      },
      // Subscription Management — owner-only. The guard is UX; the backend's
      // OWNER gates on /billing/** are the real boundary (2026-09-14 incident).
      {
        path: 'subscription',
        element: <RequireRole roles={['OWNER']} audience="the business owner"><SubscriptionPage /></RequireRole>,
      },
      {
        path: 'subscription/plans',
        element: <RequireRole roles={['OWNER']} audience="the business owner"><PlanComparisonPage /></RequireRole>,
      },
      {
        path: 'subscription/verify',
        element: <RequireRole roles={['OWNER']} audience="the business owner"><SubscriptionSuccessPage /></RequireRole>,
      },
      {
        path: 'subscription/invoices',
        element: <RequireRole roles={['OWNER']} audience="the business owner"><BillingHistoryPage /></RequireRole>,
      },
      {
        path: 'subscription/invoices/:id',
        element: <RequireRole roles={['OWNER']} audience="the business owner"><InvoiceDetailPage /></RequireRole>,
      },
      {
        path: 'subscription/payment-methods',
        element: <RequireRole roles={['OWNER']} audience="the business owner"><PaymentMethodsPage /></RequireRole>,
      },
    ],
  },

  // Staff invitation acceptance (public, no auth required)
  {
    path: '/auth/accept-invite',
    element: (
      <SuspenseWrapper>
        <AcceptInvite />
      </SuspenseWrapper>
    ),
  },

  // Public receipt view (no auth required)
  {
    path: '/receipt/:id',
    element: (
      <SuspenseWrapper>
        <PublicReceipt />
      </SuspenseWrapper>
    ),
  },

  // Public customer invoice view (no auth required) — singular path matches
  // backend's ${APP_BASE_URL}/invoice/{id} build at CustomerInvoiceService.java:192
  {
    path: '/invoice/:id',
    element: (
      <SuspenseWrapper>
        <PublicInvoicePage />
      </SuspenseWrapper>
    ),
  },

  // Public loan pack verification (no auth required)
  {
    path: '/verify/:reference',
    element: (
      <SuspenseWrapper>
        <LoanPackVerify />
      </SuspenseWrapper>
    ),
  },

  // Test routes — dev builds only; they render without auth and must never
  // ship in the production router.
  ...(import.meta.env.DEV
    ? [
        {
          path: '/test/layout',
          element: (
            <SuspenseWrapper>
              <AppLayout />
            </SuspenseWrapper>
          ),
          children: [
            {
              index: true,
              element: (
                <SuspenseWrapper>
                  <AppLayoutTest />
                </SuspenseWrapper>
              ),
            },
          ],
        },
        {
          path: '/test/metric-cards',
          element: (
            <SuspenseWrapper>
              <MetricCardTest />
            </SuspenseWrapper>
          ),
        },
      ]
    : []),

  // 404 Not Found
  {
    path: '*',
    element: (
      <SuspenseWrapper>
        <NotFound />
      </SuspenseWrapper>
    ),
  },
]);
